import { AgingReport } from '@/services/agingDashboard';
import { Box, Column } from '@palmetto/palmetto-components';

import {
  AgingBucket,
  AgingQuery,
  OneToThirtyDaysAgingEnum,
  ThirtyToOneHundredSixtyDaysAgingEnum,
  agingLabels,
} from 'types';

export interface IRowData {
  userIds: string[];
  content: string;
}

export interface ISubsection {
  title: string;
  columns: Partial<Column>[];
  rows: any[];
  rowKey: string;
}

export interface ISection {
  title: string;
  description: string;
  subsections: ISubsection[];
}

export interface ISectionToLoad {
  title: string;
  description: string;
  agingGroups: AgingQuery[];
}

interface IColumn {
  dataKey: string;
  heading: React.ReactNode;
  render: (cell: any) => React.JSX.Element;
}

const redColumnMapping = {
  [AgingQuery.ntpWithoutInstall]: [
    ThirtyToOneHundredSixtyDaysAgingEnum['91to115'],
    ThirtyToOneHundredSixtyDaysAgingEnum['116to160'],
    ThirtyToOneHundredSixtyDaysAgingEnum.greaterThan160,
  ],
  [AgingQuery.installApprovedWithoutActivationApproved]: [
    ThirtyToOneHundredSixtyDaysAgingEnum['91to115'],
    ThirtyToOneHundredSixtyDaysAgingEnum['116to160'],
    ThirtyToOneHundredSixtyDaysAgingEnum.greaterThan160,
  ],
  [AgingQuery.invoiceWithoutInstallSubmitted]: [
    OneToThirtyDaysAgingEnum['11to14'],
    OneToThirtyDaysAgingEnum['15to20'],
    OneToThirtyDaysAgingEnum['21to30'],
    OneToThirtyDaysAgingEnum.greaterThan30,
  ],
  [AgingQuery.installConditionalApprovalWithoutActivationSubmitted]: [
    OneToThirtyDaysAgingEnum['21to30'],
    OneToThirtyDaysAgingEnum.greaterThan30,
  ],
  [AgingQuery.installRejected]: [OneToThirtyDaysAgingEnum['21to30'], OneToThirtyDaysAgingEnum.greaterThan30],
  [AgingQuery.installSubmittedWithoutInvoice]: [
    OneToThirtyDaysAgingEnum['21to30'],
    OneToThirtyDaysAgingEnum.greaterThan30,
  ],
  [AgingQuery.installSubmittedWithoutActivationApproved]: [
    ThirtyToOneHundredSixtyDaysAgingEnum['61to90'],
    ThirtyToOneHundredSixtyDaysAgingEnum['91to115'],
    ThirtyToOneHundredSixtyDaysAgingEnum['116to160'],
    ThirtyToOneHundredSixtyDaysAgingEnum.greaterThan160,
  ],
  [AgingQuery.activationRejectedWithoutActivationResubmitted]: [
    OneToThirtyDaysAgingEnum['21to30'],
    OneToThirtyDaysAgingEnum.greaterThan30,
  ],
} as {
  [key: string]: string[];
};

export const SectionsToLoad: ISectionToLoad[] = [
  {
    title: 'Accounts with Potential Clawback and Net-Fund Risk',
    description: 'Days Since:',
    agingGroups: [
      AgingQuery.ntpWithoutInstall,
      AgingQuery.installSubmittedWithoutActivationApproved,
      AgingQuery.installApprovedWithoutActivationApproved,
      AgingQuery.invoiceWithoutInstallSubmitted,
    ],
  },
  {
    title: 'Accounts that Require Action',
    description: 'Days Since:',
    agingGroups: [
      AgingQuery.installSubmittedWithoutInvoice,
      AgingQuery.installConditionalApprovalWithoutActivationSubmitted,
      AgingQuery.installRejected,
      AgingQuery.activationRejectedWithoutActivationResubmitted,
    ],
  },
];

export const TextLink = ({
  title,
  agingQuery,
  bucket,
}: {
  title: string;
  agingQuery: AgingQuery;
  bucket: AgingBucket;
}) => {
  return (
    <Box
      style={{
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
      alignItems={'center'}
      fontSize={'md'}
      fontWeight={'regular'}
      onClick={() => {
        const advancedFilters = JSON.stringify([
          {
            id: 'agingAccounts',
            operation: 'Equal',
            selectedValues: [`${agingQuery}:${bucket}`],
          },
        ]);
        const baseUrl = window.location.origin;
        const encodedFilters = encodeURIComponent(`${advancedFilters}`);
        window.open(`${baseUrl}/accounts?advancedFilters=${encodedFilters}`, '_blank');
      }}
    >
      {title}
    </Box>
  );
};

export const RedTextLink = ({
  title,
  agingQuery,
  bucket,
}: {
  title: string;
  agingQuery: AgingQuery;
  bucket: AgingBucket;
}) => {
  return (
    <Box color="danger-600">
      <TextLink title={title} agingQuery={agingQuery} bucket={bucket} />
    </Box>
  );
};

export const RenderNonLinkedDataCell = (cell: any) => {
  return (
    <Box fontWeight={'medium'} fontSize={'md'} color="grey-700">
      {typeof cell?.content !== 'undefined' ? cell.content : cell}
    </Box>
  );
};

export const RenderCommonDataCell = (agingQuery: AgingQuery, bucket: AgingBucket) => {
  return function renderCommonDataCell(cell: any) {
    return (
      <Box>
        <TextLink
          title={typeof cell?.content !== 'undefined' ? cell.content : cell}
          agingQuery={agingQuery}
          bucket={bucket}
        />
      </Box>
    );
  };
};

export const RenderDangerDataCell = (agingQuery: AgingQuery, bucket: AgingBucket) => {
  return function renderDangerDataCell(cell: any) {
    return (
      <Box>
        <RedTextLink
          title={typeof cell?.content !== 'undefined' ? cell.content : cell}
          agingQuery={agingQuery}
          bucket={bucket}
        />
      </Box>
    );
  };
};

export const RenderLeftHeading = (cell: any) => {
  return (
    <Box fontWeight={'bold'} fontSize={'md'} color="grey-700">
      {cell}
    </Box>
  );
};

export const RenderHeading = (cell: any) => {
  return (
    <Box fontWeight={'bold'} alignItems={'center'} fontSize={'md'}>
      {cell}
    </Box>
  );
};

export const RenderNormalHeading = (cell: any) => {
  return <Box color="grey-700">{RenderHeading(cell)}</Box>;
};

export const RenderDangerHeading = (cell: any) => {
  return <Box color="danger-600">{RenderHeading(cell)}</Box>;
};

export const reportDataToSubsection = ({
  data,
  agingGroup,
}: {
  agingGroup: AgingQuery;
  data: AgingReport;
}): ISubsection => {
  const buckets = Object.keys(data) as AgingBucket[];
  const rows: {
    [key: string]: any;
  } = {};
  buckets.forEach((bucket) => {
    const { accountData, value: content } = data[bucket];
    rows[bucket] = {
      accountData,
      content,
    };
  });
  rows['count'] = 'Count';

  const columns: IColumn[] = [
    {
      heading: RenderLeftHeading(agingLabels[agingGroup]),
      dataKey: 'count',
      render: RenderNonLinkedDataCell,
    },
  ].concat(
    buckets.map((bucket) => {
      const isRedColumn = redColumnMapping[agingGroup]?.includes(bucket);
      const label = agingLabels[bucket] || '--';

      return {
        heading: (isRedColumn ? RenderDangerHeading(label) : RenderNormalHeading(label)) as React.JSX.Element,
        dataKey: bucket,
        render: isRedColumn ? RenderDangerDataCell(agingGroup, bucket) : RenderCommonDataCell(agingGroup, bucket),
      };
    }),
  );

  return {
    title: agingLabels[agingGroup],
    columns,
    rows: [rows],
    rowKey: agingGroup,
  };
};
